const API_URL = (process.env.REACT_APP_API_URL ?? "http://localhost:8000").replace(/\/$/, "");

const PUBLICATION_IMAGE_PATH = "/api/public/publication-images/";

/** Where the API serves images uploaded in the publication editor. */
export const PUBLICATION_IMAGE_PREFIX = `${API_URL}${PUBLICATION_IMAGE_PATH}`;

// <v4 uuid>_<width>x<height>.<png|jpg>, as the API names them (api utils/publicationImageUrl.ts).
const FILE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}_([1-9]\d{0,3})x([1-9]\d{0,3})\.(?:png|jpg)$/;

/**
 * The dimensions carried in an uploaded image's address; null for any other
 * source. For layout only, so it goes by the path: any https origin (the API's
 * may be written differently here than where the post was saved) or the
 * configured API itself (plain http on a dev machine). isSafeImageSrc, the
 * safety rule, keeps to the exact prefix.
 */
export const parsePublicationImageDims = (
  src: string | null | undefined,
): { width: number; height: number } | null => {
  if (typeof src !== "string") return null;
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  if (!url.pathname.startsWith(PUBLICATION_IMAGE_PATH)) return null;
  if (url.protocol !== "https:" && !src.startsWith(PUBLICATION_IMAGE_PREFIX)) return null;
  const match = FILE_PATTERN.exec(url.pathname.slice(PUBLICATION_IMAGE_PATH.length));
  return match ? { width: Number(match[1]), height: Number(match[2]) } : null;
};

// A portrait screenshot at the full column width would be a phone screen tall.
const PORTRAIT_MAX_WIDTH = 360;

/**
 * How an uploaded image shows in a post: never wider than the image itself,
 * and a portrait one (taller than wide) at most 360 px wide, centred. The
 * same rule as the email (api templates/publicationBlock.ts). Null for any
 * other source, which keeps the renderer's defaults.
 */
export const publicationImageBox = (
  src: string | null | undefined,
): { width: number; height: number; centred: boolean } | null => {
  const dims = parsePublicationImageDims(src);
  if (!dims) return null;
  const portrait = dims.height > dims.width;
  const width = portrait ? Math.min(PORTRAIT_MAX_WIDTH, dims.width) : dims.width;
  return { width, height: Math.round((dims.height * width) / dims.width), centred: portrait };
};
